/**
 * SetupWizard — Post-Provisioning Organization Setup
 *
 * A 5-step wizard for corporate admins to configure the org hierarchy
 * immediately after tenant provisioning.
 *
 * Steps:
 *   1. Organization  — name, industry, code
 *   2. Hierarchy     — editable tree: add regions, countries, BUs, custom layers, and sites
 *   3. Modules       — enable/disable modules per site
 *   4. Roles         — map org designations to Saarv roles
 *   5. Review        — summary with edit capability
 *
 * On completion → POST /api/org/setup-wizard (bulk creation)
 *
 * Phase 0 — Multi-Site Foundation
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrg, NODE_TYPE_COLORS, NODE_TYPE_LABELS, NODE_TYPE_CHILDREN, getNodeDisplayLabel } from '../../context/OrgContext';
import { useAlert } from '../../context/AlertContext';
import { useAuth } from '../../context/AuthContext';
import { INDUSTRIES, matchIndustry, industryLabel } from '../../utils/industryConstants';
import { MODULES } from '../../utils/moduleConstants';
import TabNavigation from '../../components/TabNavigation';
import SearchableSelect from '../../components/SearchableSelect';
import { CURRENCIES, timezoneOptions } from '../../utils/regional';

// ── Constants ─────────────────────────────────────────────────────────────────

const TOTAL_STEPS = 5;

// All node types a user can add in the wizard (organization is auto-created)
const ADDABLE_NODE_TYPES = [
  { value: 'region',        label: 'Region' },
  { value: 'country',       label: 'Country' },
  { value: 'business_unit', label: 'Business Unit' },
  { value: 'other',         label: 'Custom (user-defined)' },
  { value: 'site',          label: 'Site' },
  { value: 'sub_site',      label: 'Sub-Site' },
  { value: 'production_line', label: 'Production Line' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

let _nextId = 1;
function genId() { return `wiz-${_nextId++}`; }

function StepIndicator({ current }) {
  const steps = ['Organization', 'Hierarchy', 'Modules', 'Roles', 'Review'];
  return (
    <div className="flex items-center gap-0 mb-8 overflow-x-auto pb-1">
      {steps.map((label, i) => {
        const stepNum = i + 1;
        const done = current > stepNum;
        const active = current === stepNum;
        return (
          <React.Fragment key={label}>
            <div className="flex flex-col items-center min-w-[60px]">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-colors ${
                done    ? 'bg-green-500 border-green-500 text-white' :
                active  ? 'bg-blue-600 border-blue-600 text-white' :
                          'bg-white border-gray-300 text-gray-400'
              }`}>
                {done ? '✓' : stepNum}
              </div>
              <span className={`text-xs mt-1 text-center leading-tight ${active ? 'text-blue-600 font-medium' : 'text-gray-400'}`}>
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className={`flex-1 h-0.5 mt-[-16px] mx-1 ${done ? 'bg-green-400' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function NavButtons({ step, totalSteps, onBack, onNext, onSubmit, submitting, nextDisabled }) {
  return (
    <div className="flex justify-between items-center mt-8 pt-4 border-t">
      <button
        type="button"
        onClick={onBack}
        disabled={step === 1}
        className="px-5 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
      >
        ← Back
      </button>
      {step < totalSteps ? (
        <button
          type="button"
          onClick={onNext}
          disabled={nextDisabled}
          className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-40"
        >
          Next →
        </button>
      ) : (
        <button
          type="button"
          onClick={onSubmit}
          disabled={submitting}
          className="px-6 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-40 font-semibold"
        >
          {submitting ? 'Setting up…' : 'Complete Setup'}
        </button>
      )}
    </div>
  );
}

// ── Step 1: Organization ──────────────────────────────────────────────────────

function StepOrganization({ data, onChange }) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Your Organization</h2>
        <p className="text-sm text-gray-500 mt-1">Basic details about your company.</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Organization Name *</label>
        <input
          type="text"
          required
          value={data.name}
          onChange={(e) => onChange({ ...data, name: e.target.value })}
          placeholder="Acme Corporation"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Industry *</label>
        <select
          value={data.industry}
          onChange={(e) => onChange({ ...data, industry: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select industry…</option>
          {INDUSTRIES.map((ind) => (
            <option key={ind.value} value={ind.value}>{ind.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Short Code</label>
        <input
          type="text"
          value={data.code}
          onChange={(e) => onChange({ ...data, code: e.target.value.toUpperCase() })}
          placeholder="ACME"
          maxLength={10}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <p className="text-xs text-gray-400 mt-0.5">Used as a prefix for site codes</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Headquarters Country</label>
        <input
          type="text"
          value={data.hq_country}
          onChange={(e) => onChange({ ...data, hq_country: e.target.value })}
          placeholder="India"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
    </div>
  );
}

// ── Step 2: Editable Hierarchy Tree ───────────────────────────────────────────

/**
 * Nodes stored as flat array: [{ id, parent_id, node_type, custom_label, name, code, metadata }]
 * The org root is always nodes[0] with node_type='organization'.
 */

function EditableTreeNode({ node, allNodes, depth, onAdd, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(true);
  const children = allNodes.filter((n) => n.parent_id === node.id);
  const hasChildren = children.length > 0;
  const allowedChildTypes = NODE_TYPE_CHILDREN[node.node_type] ?? [];
  const canAddChild = allowedChildTypes.length > 0;
  const isOrg = node.node_type === 'organization';
  const displayLabel = node.node_type === 'other' && node.custom_label
    ? node.custom_label
    : (NODE_TYPE_LABELS[node.node_type] ?? node.node_type);

  return (
    <div>
      <div
        className="flex items-center gap-1.5 py-1.5 px-2 rounded-md group hover:bg-gray-50"
        style={{ paddingLeft: `${depth * 20 + 8}px` }}
      >
        {/* Expand toggle */}
        <button
          onClick={() => setExpanded((v) => !v)}
          className={`flex-shrink-0 ${hasChildren ? 'cursor-pointer' : 'invisible'}`}
        >
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {/* Icon */}
        <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>

        {/* Name */}
        <span className="font-medium text-gray-800 text-sm flex-1 truncate">
          {node.name}
          {node.code && <span className="ml-1.5 text-xs text-gray-400 font-mono">{node.code}</span>}
          {node.metadata?.is_hq && (
            <span className="ml-1.5 text-xs bg-amber-100 text-amber-700 px-1 rounded">HQ</span>
          )}
        </span>

        {/* Badge */}
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${NODE_TYPE_COLORS[node.node_type] ?? 'bg-gray-100 text-gray-600'}`}>
          {displayLabel}
        </span>

        {/* Action buttons */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
          {canAddChild && (
            <button onClick={() => onAdd(node)} title="Add child"
              className="p-1 rounded text-green-600 hover:bg-green-50">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          )}
          {!isOrg && (
            <>
              <button onClick={() => onEdit(node)} title="Edit"
                className="p-1 rounded text-blue-600 hover:bg-blue-50">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              <button onClick={() => onDelete(node)} title="Delete"
                className="p-1 rounded text-red-500 hover:bg-red-50">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Children */}
      {hasChildren && expanded && (
        <div className="border-l border-gray-200" style={{ marginLeft: `${depth * 20 + 18}px` }}>
          {children.map((child) => (
            <EditableTreeNode
              key={child.id}
              node={child}
              allNodes={allNodes}
              depth={depth + 1}
              onAdd={onAdd}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Inline form modal for adding/editing a node in the wizard tree */
function WizardNodeForm({ node, parentNode, onClose, onSave }) {
  const isEdit = !!node;
  const allowedTypes = parentNode
    ? (NODE_TYPE_CHILDREN[parentNode.node_type] ?? [])
    : ['organization'];

  const [formData, setFormData] = useState({
    node_type: node?.node_type ?? allowedTypes[0] ?? 'site',
    custom_label: node?.custom_label ?? '',
    name: node?.name ?? '',
    code: node?.code ?? '',
    is_hq: node?.metadata?.is_hq ?? false,
    address: node?.metadata?.address ?? '',
    timezone: node?.metadata?.timezone ?? '',
    currency: node?.metadata?.currency ?? '',
  });

  const set = (k, v) => setFormData((p) => ({ ...p, [k]: v }));
  const isSite = formData.node_type === 'site' || formData.node_type === 'sub_site';

  const handleSubmit = (e) => {
    e.preventDefault();
    const result = {
      node_type: formData.node_type,
      custom_label: formData.node_type === 'other' ? formData.custom_label : null,
      name: formData.name,
      code: formData.code || null,
      metadata: {},
    };
    if (isSite) {
      result.metadata = {
        is_hq: formData.is_hq,
        ...(formData.address && { address: formData.address }),
        ...(formData.timezone && { timezone: formData.timezone }),
        ...(formData.currency && { currency: formData.currency }),
      };
    }
    onSave(result);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full shadow-xl">
        <h2 className="text-lg font-bold text-gray-900 mb-4">
          {isEdit ? `Edit: ${node.name}` : `Add child to "${parentNode?.name}"`}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Node Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select value={formData.node_type} onChange={(e) => set('node_type', e.target.value)}
              disabled={isEdit} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm disabled:bg-gray-50">
              {(isEdit ? ADDABLE_NODE_TYPES : ADDABLE_NODE_TYPES.filter((t) => allowedTypes.includes(t.value))).map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          {/* Custom Label for "other" */}
          {formData.node_type === 'other' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Layer Label *</label>
              <input type="text" required value={formData.custom_label}
                onChange={(e) => set('custom_label', e.target.value)}
                placeholder="e.g. Division, Zone, Plant Group" maxLength={100}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input type="text" required value={formData.name}
              onChange={(e) => set('name', e.target.value)} placeholder="e.g. APAC, Mumbai Plant"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
          </div>

          {/* Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
            <input type="text" value={formData.code}
              onChange={(e) => set('code', e.target.value.toUpperCase())} placeholder="e.g. APAC-01"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono" />
          </div>

          {/* Site-specific fields */}
          {isSite && (
            <>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="wiz_is_hq" checked={formData.is_hq}
                  onChange={(e) => set('is_hq', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600" />
                <label htmlFor="wiz_is_hq" className="text-sm text-gray-700">This is the HQ site</label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input type="text" value={formData.address} onChange={(e) => set('address', e.target.value)}
                  placeholder="Full site address" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                  {/* REG-01: IANA list from regional.js (no free text, no India default) */}
                  <SearchableSelect
                    value={formData.timezone}
                    onChange={(val) => set('timezone', val || '')}
                    options={timezoneOptions(formData.timezone)}
                    placeholder="Select timezone..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                  <select value={formData.currency}
                    onChange={(e) => set('currency', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                    <option value="">Not set</option>
                    {formData.currency && !CURRENCIES.some((c) => c.code === formData.currency) && (
                      <option value={formData.currency}>{formData.currency}</option>
                    )}
                    {CURRENCIES.map((c) => (
                      <option key={c.code} value={c.code}>{c.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}

          <div className="flex gap-2 justify-end pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">
              {isEdit ? 'Save Changes' : 'Add Node'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function StepHierarchyTree({ treeNodes, onChange, orgName }) {
  const [showForm, setShowForm] = useState(false);
  const [editingNode, setEditingNode] = useState(null);
  const [parentNode, setParentNode] = useState(null);

  // Ensure org root exists
  useEffect(() => {
    if (treeNodes.length === 0) {
      onChange([{ id: genId(), parent_id: null, node_type: 'organization', name: orgName || 'Organization', code: null, custom_label: null, metadata: {} }]);
    } else if (treeNodes[0]?.name !== orgName && orgName) {
      onChange(treeNodes.map((n, i) => i === 0 ? { ...n, name: orgName } : n));
    }
  }, [orgName]); // intentionally omit onChange/treeNodes to avoid infinite loop

  const handleAdd = (parent) => {
    setEditingNode(null);
    setParentNode(parent);
    setShowForm(true);
  };

  const handleEdit = (node) => {
    setEditingNode(node);
    setParentNode(null);
    setShowForm(true);
  };

  const handleDelete = (node) => {
    // Recursively collect IDs to delete
    const idsToDelete = new Set();
    const collect = (id) => {
      idsToDelete.add(id);
      treeNodes.filter((n) => n.parent_id === id).forEach((n) => collect(n.id));
    };
    collect(node.id);
    onChange(treeNodes.filter((n) => !idsToDelete.has(n.id)));
  };

  const handleSave = (formResult) => {
    if (editingNode) {
      // Update existing node
      onChange(treeNodes.map((n) => n.id === editingNode.id ? { ...n, ...formResult } : n));
    } else {
      // Add new node
      const newNode = {
        id: genId(),
        parent_id: parentNode.id,
        ...formResult,
      };
      onChange([...treeNodes, newNode]);
    }
    setShowForm(false);
    setEditingNode(null);
    setParentNode(null);
  };

  const orgRoot = treeNodes.find((n) => n.node_type === 'organization');
  const siteCount = treeNodes.filter((n) => n.node_type === 'site').length;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Build Your Hierarchy</h2>
        <p className="text-sm text-gray-500 mt-1">
          Click the <span className="text-green-600 font-bold">+</span> button on any node to add
          children — regions, countries, business units, custom layers, or sites.
          You must add at least one site.
        </p>
      </div>

      {/* Editable tree */}
      <div className="border border-gray-200 rounded-lg bg-white p-4 min-h-[200px]">
        {orgRoot ? (
          <EditableTreeNode
            node={orgRoot}
            allNodes={treeNodes}
            depth={0}
            onAdd={handleAdd}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        ) : (
          <div className="text-center text-gray-400 py-8 text-sm">Loading...</div>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-2 text-xs">
        {[
          { label: 'Organization', color: NODE_TYPE_COLORS.organization },
          { label: 'Region', color: NODE_TYPE_COLORS.region },
          { label: 'Country', color: NODE_TYPE_COLORS.country },
          { label: 'Business Unit', color: NODE_TYPE_COLORS.business_unit },
          { label: 'Custom', color: NODE_TYPE_COLORS.other },
          { label: 'Site', color: NODE_TYPE_COLORS.site },
        ].map(({ label, color }) => (
          <span key={label} className={`px-2 py-0.5 rounded-full font-medium ${color}`}>{label}</span>
        ))}
      </div>

      {/* Validation hint */}
      {siteCount === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg text-sm">
          Add at least one site to your hierarchy to proceed.
        </div>
      )}

      {/* Node form modal */}
      {showForm && (
        <WizardNodeForm
          node={editingNode}
          parentNode={parentNode || (editingNode ? treeNodes.find((n) => n.id === editingNode.parent_id) : null)}
          onClose={() => { setShowForm(false); setEditingNode(null); setParentNode(null); }}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

// ── Step 3: Module Configuration ──────────────────────────────────────────────

function StepModules({ allSites, moduleConfig, onChange }) {
  const toggle = (siteKey, slug) => {
    const current = moduleConfig[siteKey] ?? new Set(MODULES.map((m) => m.slug));
    const updated = new Set(current);
    if (updated.has(slug)) updated.delete(slug); else updated.add(slug);
    onChange({ ...moduleConfig, [siteKey]: updated });
  };

  const toggleAll = (siteKey) => {
    const current = moduleConfig[siteKey] ?? new Set(MODULES.map((m) => m.slug));
    if (current.size === MODULES.length) {
      onChange({ ...moduleConfig, [siteKey]: new Set() });
    } else {
      onChange({ ...moduleConfig, [siteKey]: new Set(MODULES.map((m) => m.slug)) });
    }
  };

  const copyToAll = (siteKey) => {
    const source = moduleConfig[siteKey] ?? new Set(MODULES.map((m) => m.slug));
    const updated = {};
    allSites.forEach((s) => { updated[s.key] = new Set(source); });
    onChange(updated);
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Module Configuration</h2>
        <p className="text-sm text-gray-500 mt-1">
          Enable or disable modules for each site. All modules are enabled by default.
        </p>
      </div>

      {allSites.length === 0 && (
        <div className="text-center text-gray-400 text-sm py-4">No sites defined yet. Go back and add sites to your hierarchy.</div>
      )}

      {allSites.map((site) => {
        const enabled = moduleConfig[site.key] ?? new Set(MODULES.map((m) => m.slug));
        const allOn = enabled.size === MODULES.length;
        return (
          <div key={site.key} className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 flex items-center justify-between">
              <div>
                <span className="font-medium text-gray-800">{site.name}</span>
                {site.isHQ && <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">HQ</span>}
                {site.code && <span className="ml-2 text-xs text-gray-400 font-mono">{site.code}</span>}
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => copyToAll(site.key)}
                  className="text-xs text-gray-500 hover:text-gray-700 border border-gray-300 px-2 py-1 rounded">Copy to all</button>
                <button type="button" onClick={() => toggleAll(site.key)}
                  className="text-xs text-blue-600 hover:text-blue-800 border border-blue-200 px-2 py-1 rounded">
                  {allOn ? 'Disable all' : 'Enable all'}
                </button>
              </div>
            </div>
            <div className="p-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
              {MODULES.map((mod) => (
                <label key={mod.slug} className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={enabled.has(mod.slug)}
                    onChange={() => toggle(site.key, mod.slug)}
                    className="rounded border-gray-300 text-blue-600" />
                  <span className="text-sm text-gray-700"><span className="mr-1">{mod.icon}</span>{mod.label}</span>
                </label>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Step 4: Role Mapping ──────────────────────────────────────────────────────

function StepRoles({ allSites, roleMappings, onChange, systemRoles }) {
  const getSiteMappings = (siteKey) => roleMappings[siteKey] || [];
  const updateSite = (siteKey, mappings) => onChange({ ...roleMappings, [siteKey]: mappings });
  const addMapping = (siteKey) => updateSite(siteKey, [...getSiteMappings(siteKey), { designation: '', systemRoleId: '' }]);
  const removeMapping = (siteKey, i) => updateSite(siteKey, getSiteMappings(siteKey).filter((_, idx) => idx !== i));
  const updateMapping = (siteKey, i, field, value) =>
    updateSite(siteKey, getSiteMappings(siteKey).map((r, idx) => idx === i ? { ...r, [field]: value } : r));
  const copyToAll = (sourceSiteKey) => {
    const source = getSiteMappings(sourceSiteKey);
    const updated = {};
    allSites.forEach((site) => { updated[site.key] = source.map((r) => ({ ...r })); });
    onChange(updated);
  };

  const sysRoles = systemRoles.filter((r) => r.is_system);
  const customRoles = systemRoles.filter((r) => !r.is_system);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Role Mapping</h2>
        <p className="text-sm text-gray-500 mt-1">
          Map your organisation's job titles to Saarv system roles for each site. Optional —
          you can configure roles later from Admin → Role Management.
        </p>
      </div>

      {allSites.map((site) => {
        const mappings = getSiteMappings(site.key);
        return (
          <div key={site.key} className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 flex items-center justify-between">
              <div>
                <span className="font-medium text-gray-800">{site.name}</span>
                {site.isHQ && <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">HQ</span>}
                {site.code && <span className="ml-2 text-xs text-gray-400 font-mono">{site.code}</span>}
              </div>
              <button type="button" onClick={() => copyToAll(site.key)}
                className="text-xs text-gray-500 hover:text-gray-700 border border-gray-300 px-2 py-1 rounded">Copy to all sites</button>
            </div>
            <div className="p-4 space-y-2">
              {mappings.length === 0 ? (
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center text-gray-400 text-sm">
                  No role mappings for this site.
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-[1fr_1fr_auto] gap-3 px-1 mb-1">
                    <span className="text-xs font-medium text-gray-500 uppercase">Site Designation</span>
                    <span className="text-xs font-medium text-gray-500 uppercase">System Role</span>
                    <span />
                  </div>
                  {mappings.map((mapping, i) => (
                    <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-3 items-center">
                      <input type="text" value={mapping.designation}
                        onChange={(e) => updateMapping(site.key, i, 'designation', e.target.value)}
                        placeholder="e.g. Plant Manager"
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      <select value={mapping.systemRoleId}
                        onChange={(e) => updateMapping(site.key, i, 'systemRoleId', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="">Select system role…</option>
                        {sysRoles.length > 0 && (
                          <optgroup label="System Roles">
                            {sysRoles.map((r) => <option key={r.id} value={r.id}>{r.display_name || r.name}</option>)}
                          </optgroup>
                        )}
                        {customRoles.length > 0 && (
                          <optgroup label="Custom Roles">
                            {customRoles.map((r) => <option key={r.id} value={r.id}>{r.display_name || r.name}</option>)}
                          </optgroup>
                        )}
                      </select>
                      <button type="button" onClick={() => removeMapping(site.key, i)}
                        className="text-red-400 hover:text-red-600 text-lg leading-none px-1">×</button>
                    </div>
                  ))}
                </>
              )}
              <button type="button" onClick={() => addMapping(site.key)}
                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 mt-2">
                <span className="text-lg leading-none">+</span> Add Role Mapping
              </button>
            </div>
          </div>
        );
      })}

      {systemRoles.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg text-sm">
          No system roles found. Roles can be configured later from Admin → Role Management.
        </div>
      )}
    </div>
  );
}

// ── Step 5: Review ────────────────────────────────────────────────────────────

function ReviewTreeNode({ node, allNodes, depth }) {
  const children = allNodes.filter((n) => n.parent_id === node.id);
  const displayLabel = node.node_type === 'other' && node.custom_label
    ? node.custom_label
    : (NODE_TYPE_LABELS[node.node_type] ?? node.node_type);

  return (
    <>
      <div className="flex items-center gap-1.5 py-1" style={{ paddingLeft: `${depth * 16}px` }}>
        <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
        <span className="text-sm text-gray-800 font-medium">{node.name}</span>
        {node.code && <span className="text-xs text-gray-400 font-mono">{node.code}</span>}
        {node.metadata?.is_hq && <span className="text-xs bg-amber-100 text-amber-700 px-1 rounded">HQ</span>}
        <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${NODE_TYPE_COLORS[node.node_type] ?? 'bg-gray-100 text-gray-600'}`}>
          {displayLabel}
        </span>
      </div>
      {children.map((child) => (
        <ReviewTreeNode key={child.id} node={child} allNodes={allNodes} depth={depth + 1} />
      ))}
    </>
  );
}

function StepReview({ org, treeNodes, moduleConfig, roleMappings, systemRoles, allSites, onGoToStep }) {
  const orgRoot = treeNodes.find((n) => n.node_type === 'organization');

  const SectionHeader = ({ title, step }) => (
    <div className="flex items-center justify-between mb-3">
      <h3 className="font-semibold text-gray-800">{title}</h3>
      <button type="button" onClick={() => onGoToStep(step)}
        className="text-xs text-blue-600 hover:underline">Edit</button>
    </div>
  );

  const getRoleName = (roleId) => {
    const found = systemRoles.find((r) => r.id === roleId);
    return found ? (found.display_name || found.name) : roleId;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Review & Confirm</h2>
        <p className="text-sm text-gray-500 mt-1">
          Everything looks good? Click "Complete Setup" to provision your organization hierarchy.
        </p>
      </div>

      {/* Organization */}
      <div className="bg-gray-50 rounded-lg p-4">
        <SectionHeader title="Organization" step={1} />
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <dt className="text-gray-500">Name</dt><dd className="text-gray-800 font-medium">{org.name || '—'}</dd>
          <dt className="text-gray-500">Industry</dt><dd className="text-gray-800">{industryLabel(org.industry) || '—'}</dd>
          <dt className="text-gray-500">Code</dt><dd className="text-gray-800 font-mono">{org.code || '—'}</dd>
          <dt className="text-gray-500">HQ Country</dt><dd className="text-gray-800">{org.hq_country || '—'}</dd>
        </dl>
      </div>

      {/* Hierarchy tree */}
      <div className="bg-gray-50 rounded-lg p-4">
        <SectionHeader title="Organization Structure" step={2} />
        {orgRoot && <ReviewTreeNode node={orgRoot} allNodes={treeNodes} depth={0} />}
      </div>

      {/* Modules */}
      <div className="bg-gray-50 rounded-lg p-4">
        <SectionHeader title="Module Configuration" step={3} />
        {allSites.map((site) => {
          const enabled = moduleConfig[site.key] ?? new Set(MODULES.map((m) => m.slug));
          return (
            <div key={site.key} className="mb-2 last:mb-0">
              <p className="text-sm font-medium text-gray-700">
                {site.name} {site.isHQ && <span className="text-xs text-amber-600">(HQ)</span>}
              </p>
              <p className="text-xs text-gray-500">
                {enabled.size === MODULES.length ? 'All modules enabled' : `${enabled.size} of ${MODULES.length} modules enabled`}
              </p>
            </div>
          );
        })}
      </div>

      {/* Role Mappings */}
      {(() => {
        const totalMappings = allSites.reduce((sum, site) => {
          const m = (roleMappings[site.key] || []).filter((r) => r.designation && r.systemRoleId);
          return sum + m.length;
        }, 0);
        if (totalMappings === 0) return null;
        return (
          <div className="bg-gray-50 rounded-lg p-4">
            <SectionHeader title={`Role Mappings (${totalMappings})`} step={4} />
            {allSites.map((site) => {
              const mappings = (roleMappings[site.key] || []).filter((r) => r.designation && r.systemRoleId);
              if (mappings.length === 0) return null;
              return (
                <div key={site.key} className="mb-3 last:mb-0">
                  <p className="text-sm font-medium text-gray-700 mb-1">
                    {site.name} {site.isHQ && <span className="text-xs text-amber-600">(HQ)</span>}
                  </p>
                  <ul className="space-y-1 ml-3">
                    {mappings.map((r, i) => (
                      <li key={i} className="text-sm text-gray-700">
                        {r.designation} → <span className="font-medium">{getRoleName(r.systemRoleId)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        );
      })()}
    </div>
  );
}

// ── Main Wizard Component ─────────────────────────────────────────────────────

export default function SetupWizard({ user, onLogout, hideNavigation = false, onComplete }) {
  const navigate = useNavigate();
  const { runSetupWizard } = useOrg();
  const { showSuccess, showError } = useAlert();
  const { tenant } = useAuth();

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  // System roles fetched from API
  const [systemRoles, setSystemRoles] = useState([]);
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const token = localStorage.getItem('token');
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${backendUrl}/api/tenant-admin/roles`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setSystemRoles(data || []);
        }
      } catch (err) {
        console.log('Failed to fetch system roles:', err);
      }
    };
    fetchRoles();
  }, []);

  // Step 1: Organization data
  const [org, setOrg] = useState({
    name: tenant?.name || tenant?.company_name || '',
    industry: matchIndustry(tenant?.industry),
    code: '',
    hq_country: '',
  });

  // Step 2: Tree nodes (flat array with parent_id references)
  const [treeNodes, setTreeNodes] = useState([]);

  // Steps 3-4: Module config and role mappings
  const [moduleConfig, setModuleConfig] = useState({});
  const [roleMappings, setRoleMappings] = useState({});

  // Derived: all sites from treeNodes for module/role steps
  const allSitesForConfig = treeNodes
    .filter((n) => n.node_type === 'site' || n.node_type === 'sub_site')
    .map((n) => ({
      key: n.id,
      name: n.name || 'Site',
      code: n.code,
      isHQ: n.metadata?.is_hq ?? false,
    }));

  const canProceed = useCallback(() => {
    if (step === 1) return !!org.name && !!org.industry;
    if (step === 2) return treeNodes.some((n) => n.node_type === 'site');
    return true;
  }, [step, org, treeNodes]);

  const handleNext = () => setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  const handleBack = () => setStep((s) => Math.max(s - 1, 1));

  const buildPayload = () => {
    const nodes = [];
    const idMap = {}; // wizard id -> temp_id for parent references

    // Process nodes in parent-first order (BFS)
    const queue = [null]; // start with root (parent_id === null)
    const visited = new Set();
    let sortCounter = 0;

    while (queue.length > 0) {
      const currentParentId = queue.shift();
      const children = treeNodes.filter((n) => n.parent_id === currentParentId);

      for (const node of children) {
        if (visited.has(node.id)) continue;
        visited.add(node.id);

        const tempId = `node-${sortCounter}`;
        idMap[node.id] = tempId;

        const payload = {
          temp_id: tempId,
          node_type: node.node_type,
          name: node.name,
          sort_order: sortCounter,
        };

        if (node.parent_id && idMap[node.parent_id]) {
          payload.parent_temp_id = idMap[node.parent_id];
        }

        if (node.code) payload.code = node.code;
        if (node.custom_label) payload.custom_label = node.custom_label;

        // Build metadata
        const meta = { ...(node.metadata || {}) };
        if (node.node_type === 'organization') {
          meta.industry = org.industry;
          meta.hq_country = org.hq_country;
        }
        if (node.node_type === 'site' || node.node_type === 'sub_site') {
          meta.modules = Array.from(moduleConfig[node.id] ?? new Set(MODULES.map((m) => m.slug)));
          meta.role_mappings = (roleMappings[node.id] || []).filter((r) => r.designation && r.systemRoleId);
        }
        payload.metadata = meta;

        nodes.push(payload);
        sortCounter++;
        queue.push(node.id);
      }
    }

    return { nodes };
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await runSetupWizard(buildPayload());
      showSuccess('Organization hierarchy set up successfully!');
      if (onComplete) {
        onComplete();
      } else {
        navigate('/admin/org-hierarchy');
      }
    } catch (err) {
      showError(`Setup failed: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1: return <StepOrganization data={org} onChange={setOrg} />;
      case 2: return <StepHierarchyTree treeNodes={treeNodes} onChange={setTreeNodes} orgName={org.name} />;
      case 3: return <StepModules allSites={allSitesForConfig} moduleConfig={moduleConfig} onChange={setModuleConfig} />;
      case 4: return <StepRoles allSites={allSitesForConfig} roleMappings={roleMappings} onChange={setRoleMappings} systemRoles={systemRoles} />;
      case 5: return (
        <StepReview
          org={org}
          treeNodes={treeNodes}
          moduleConfig={moduleConfig}
          roleMappings={roleMappings}
          systemRoles={systemRoles}
          allSites={allSitesForConfig}
          onGoToStep={setStep}
        />
      );
      default: return null;
    }
  };

  const content = (
    <div className={hideNavigation ? '' : 'max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10'}>
      {/* Header */}
      <div className="mb-6">
        <h1 className={`font-bold text-gray-900 ${hideNavigation ? 'text-2xl' : 'text-3xl'}`}>Organization Setup</h1>
        <p className="text-gray-500 mt-1">
          Configure your organization hierarchy to enable multi-site management across all Saarv modules.
        </p>
      </div>

      {/* Step indicator */}
      <StepIndicator current={step} />

      {/* Step content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 min-h-[300px]">
        {renderStep()}

        <NavButtons
          step={step}
          totalSteps={TOTAL_STEPS}
          onBack={handleBack}
          onNext={handleNext}
          onSubmit={handleSubmit}
          submitting={submitting}
          nextDisabled={!canProceed()}
        />
      </div>

      {/* Skip link */}
      {step < TOTAL_STEPS && !onComplete && (
        <p className="text-center mt-4 text-sm text-gray-400">
          <button type="button" onClick={() => navigate('/admin/org-hierarchy')}
            className="hover:text-gray-600 underline">
            Skip for now — I'll configure this later
          </button>
        </p>
      )}
    </div>
  );

  if (hideNavigation) {
    return content;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      {content}
    </div>
  );
}
